// src/components/Auth.tsx
// Updated: 2026-08-20 15:00:00 CDT
// Purpose: Public Supabase sign-in with password-recovery access; registration is disabled.

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Film,
  Lock,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { startGoogleSignIn } from "@/utils/googleSignIn";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [signInError, setSignInError] = useState("");

  const handleGoogleSignIn = async () => {
    if (loading || googleLoading) return;
    setGoogleLoading(true);
    setSignInError("");
    try {
      await startGoogleSignIn();
    } catch {
      setSignInError("Google sign-in is unavailable. Please use your email and password or try again later.");
      setGoogleLoading(false);
    }
  };

  const handleSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      console.debug("[Auth] Sign-in completed.");
      toast.success("Welcome to TableRead.tv!");
    } catch (error) {
      console.error("[Auth] Sign-in failed:", error);
      toast.error(error instanceof Error ? error.message : "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card/50 backdrop-blur-sm border-primary/10">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Film className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-bold">TableRead.tv</h1>
          </div>
          <CardTitle>Welcome Back</CardTitle>
          <CardDescription>
            Sign in to bring your screenplays to life with AI voices
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Button type="button" variant="outline" className="mb-4 w-full" disabled={loading || googleLoading}
            onClick={() => void handleGoogleSignIn()}>
            {googleLoading ? "Opening Google…" : "Continue with Google"}
          </Button>
          <p className="mb-4 text-center text-sm text-muted-foreground">or sign in with email</p>
          {(signInError || searchParams.has("error") || new URLSearchParams(window.location.hash.slice(1)).has("error")) && <p role="alert" className="mb-4 text-sm text-destructive">
            {signInError || "Sign-in could not be completed. Please try again or use your email and password."}
          </p>}
          {searchParams.get("password") === "updated" && (
            <p className="mb-4 rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm" role="status">
              Your password was updated. Sign in with the new password.
            </p>
          )}

          <form
            onSubmit={handleSignIn}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="writer@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Your password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || googleLoading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            <Button asChild type="button" variant="outline" className="w-full">
              <Link to="/forgot-password">Forgot password?</Link>
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              New account registration is temporarily unavailable.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
